import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../ShutdownRitual.module.css';

interface CaptureStepProps {
  capturedTasks: string[];
  onAddTask: (task: string) => void;
  onRemoveTask: (index: number) => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function CaptureStep({ capturedTasks, onAddTask, onRemoveTask }: CaptureStepProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        onAddTask(trimmed);
        setInputValue('');
        inputRef.current?.focus();
      }
    },
    [inputValue, onAddTask]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Capture Loose Thoughts
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Is there anything lingering in your mind? Tasks, ideas, reminders - capture them here so you
        can truly shut down.
      </motion.p>

      {/* Input Form */}
      <motion.form className={styles.quickAddForm} onSubmit={handleSubmit} variants={itemVariants}>
        <input
          ref={inputRef}
          type="text"
          className={styles.quickAddInput}
          placeholder="What's on your mind?"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Task or thought to capture"
        />
        <motion.button
          type="submit"
          className={styles.quickAddBtn}
          disabled={!inputValue.trim()}
          whileHover={{ scale: inputValue.trim() ? 1.02 : 1 }}
          whileTap={{ scale: inputValue.trim() ? 0.98 : 1 }}
        >
          Add
        </motion.button>
      </motion.form>

      {/* Captured Tasks List */}
      <motion.div className={styles.taskList} variants={itemVariants}>
        <AnimatePresence mode="popLayout">
          {capturedTasks.map((task, index) => (
            <motion.div
              key={`${task}-${index}`}
              className={styles.taskItem}
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, x: -100 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              layout
            >
              <span className={styles.taskText}>{task}</span>
              <motion.button
                className={styles.removeBtn}
                onClick={() => onRemoveTask(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`Remove task: ${task}`}
              >
                Remove
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Empty state */}
      {capturedTasks.length === 0 && (
        <motion.div
          className={styles.emptyState}
          variants={itemVariants}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <p>No tasks captured yet. If your mind is clear, feel free to move on!</p>
        </motion.div>
      )}

      {/* Count */}
      {capturedTasks.length > 0 && (
        <motion.p
          style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '16px' }}
          variants={itemVariants}
        >
          {capturedTasks.length} task{capturedTasks.length !== 1 ? 's' : ''} captured
        </motion.p>
      )}
    </motion.div>
  );
}

export default CaptureStep;
